import { 
  collection,
  doc, 
  getDoc,
  getDocs,
  query,
  where,
  addDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  runTransaction,
  deleteField
} from 'firebase/firestore';
import { db, auth } from './config';
import { updateUserReputation, ACTIVITY_TYPES } from './userReputation';
import { addFeature } from './features'; // Only addFeature is needed here now for ensuring definition
// mapFeature is removed as predictions from frontend should be pre-processed
// import { mapFeature, normalizeFeature } from '../utils/featureUtils'; // No longer using mapFeature here

// Get all media for a site
export const getSiteMedia = async (siteId) => {
  // ... (existing getSiteMedia code remains largely unchanged) ...
  console.log('getSiteMedia called with siteId:', siteId); 
  try {
    if (!siteId) {
      console.error('getSiteMedia called with null/undefined siteId');
      return { success: false, error: 'Site ID is required' };
    }

    const mediaQuery = query(
      collection(db, 'site_media'),
      where('siteId', '==', siteId)
    );
    const mediaSnap = await getDocs(mediaQuery);
    const mediaData = mediaSnap.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    const validatedMedia = mediaData
      .filter(item => item.dmcaStatus !== 'taken_down')
      .map(item => ({
        id: item.id,
        url: item.url || '',
        fileName: item.fileName || 'Untitled',
        type: item.type || 'image',
        title: item.title || item.fileName || 'Untitled',
        uploadedAt: item.uploadedAt || null,
        predictions: item.predictions || [],
        isMain: item.isMain || false,
        subLocation: item.subLocation || null,
        subLocationId: item.subLocationId || null,
        coordinates: item.coordinates || null,
        path: item.path || item.url || '',
        credits: item.credits || null,
        creditProfileId: item.creditProfileId || null,
      }));
    
    return {
      success: true,
      data: validatedMedia
    };
  } catch (error) {
    console.error('Error getting site media:', error);
    return { 
      success: false, 
      error: `Failed to get site media: ${error.message}`
    };
  }
};

// Add media to a site
export const addSiteMedia = async (siteId, mediaData) => {
  try {
    const siteRef = doc(db, 'sites', siteId);
    const siteSnap = await getDoc(siteRef);
    
    if (!siteSnap.exists()) {
      return { success: false, error: 'Site not found' };
    }

    const mediaPromises = mediaData.items.map(async (item) => {
      const cleanedItem = {
        siteId,
        url: item.url || '',
        path: item.path || '',
        type: item.type || 'image',
        name: item.name || '',
        size: item.size || 0,
        uploadedAt: serverTimestamp(),
        uploadedBy: item.uploadedBy || auth.currentUser?.uid || 'anonymous',
        subLocationId: item.sublocationId || null,
        coordinates: item.coordinates || null,
        isMain: false,
        predictions: [], // Initialize as empty
        title: item.title || item.name || 'Untitled',
        credits: item.credits || null,
        creditProfileId: item.creditProfileId || null,
        dmcaStatus: 'active',
      };

      // Process item.predictions (assumed to be from FeatureTaggingModal, already containing id, label, category)
      if (item.predictions && Array.isArray(item.predictions)) {
        const validatedItemPredictions = [];
        for (const pred of item.predictions) {
          if (!pred.class || !pred.label || !pred.category) {
            console.warn('Skipping prediction in addSiteMedia due to missing class, label, or category:', pred);
            continue;
          }
          validatedItemPredictions.push({
            class: pred.class, // Standardized ID from modal
            label: pred.label, // Label from modal
            category: pred.category, // Category from modal
            source: pred.source || 'manual',
            confidence: typeof pred.confidence === 'number' ? pred.confidence : 1,
            x: typeof pred.x === 'number' ? pred.x : 0,
            y: typeof pred.y === 'number' ? pred.y : 0,
            width: typeof pred.width === 'number' ? pred.width : 0,
            height: typeof pred.height === 'number' ? pred.height : 0
          });

          // Ensure the feature definition exists/is up-to-date in Firestore.
          await addFeature({
            label: pred.label,
            category: pred.category,
            source: pred.source || 'manual'
          });
        }
        cleanedItem.predictions = validatedItemPredictions;
      }
      return addDoc(collection(db, 'site_media'), cleanedItem);
    });

    const mediaRefs = await Promise.all(mediaPromises);

    const firstMediaInfo = mediaRefs.length > 0 && mediaData.items.length > 0 && mediaData.items[0].url
      ? { id: mediaRefs[0].id, url: mediaData.items[0].url } 
      : null;

    await runTransaction(db, async (transaction) => {
      const siteDoc = await transaction.get(siteRef);
      if (!siteDoc.exists()) throw new Error('Site not found');
      const currentSiteData = siteDoc.data();

      const mediaQuery = query(collection(db, 'site_media'), where('siteId', '==', siteId));
      const mediaSnap = await getDocs(mediaQuery); // Use getDocs on the query directly
      
      const aggregatedFeatures = {};
      mediaSnap.docs.forEach(doc => {
        const mediaPredictions = doc.data().predictions || [];
        mediaPredictions.forEach(pred => {
          const featureId = pred.class;
          const label = pred.label;
          const category = pred.category;
          if (!featureId || featureId === 'unknown') return;
          
          if (!aggregatedFeatures[featureId]) {
            aggregatedFeatures[featureId] = {
              enabled: true,
              autoDetected: pred.source === 'auto',
              confidence: pred.confidence || 1,
              label: label || featureId,
              category: category || 'uncategorized'
            };
          } else {
            if (pred.confidence && pred.confidence > (aggregatedFeatures[featureId].confidence || 0)) {
              aggregatedFeatures[featureId].confidence = pred.confidence;
            }
            if (aggregatedFeatures[featureId].autoDetected && pred.source !== 'auto') {
              aggregatedFeatures[featureId].autoDetected = false;
            }
            if (category && category !== 'uncategorized' && aggregatedFeatures[featureId].category === 'uncategorized') {
              aggregatedFeatures[featureId].category = category;
            }
          }
        });
      });

      const siteUpdates = {
        features: aggregatedFeatures, // This is the site-level summary
        updatedAt: serverTimestamp(),
        lastMediaUpdate: serverTimestamp()
      };

      if (firstMediaInfo && !currentSiteData.mainImage && !currentSiteData.primaryMediaId) {
        siteUpdates.mainImage = firstMediaInfo.url;
        siteUpdates.primaryMediaId = firstMediaInfo.id;
        const firstMediaDocRef = doc(db, 'site_media', firstMediaInfo.id);
        transaction.update(firstMediaDocRef, { isMain: true });
      }
      transaction.update(siteRef, siteUpdates);
    });

    if (auth.currentUser && mediaRefs.length > 0) {
      await updateUserReputation(auth.currentUser.uid, ACTIVITY_TYPES.MEDIA_UPLOAD, mediaRefs.length);
    }
    return { success: true, mediaIds: mediaRefs.map(ref => ref.id) };
  } catch (error) {
    console.error('Error adding site media:', error);
    return { success: true, warning: 'Image uploaded but metadata update failed.', error: error.message };
  }
};

// Update media predictions (e.g., when user manually tags an image)
export const updateMediaFeatures = async (siteId, mediaId, predictionsFromFrontend) => {
  try {
    if (!siteId || !mediaId || !Array.isArray(predictionsFromFrontend)) {
      return { success: false, error: 'Invalid input parameters' };
    }

    const mediaRef = doc(db, 'site_media', mediaId);
    const mediaDocSnap = await getDoc(mediaRef); // Use getDoc for single document

    if (!mediaDocSnap.exists() || mediaDocSnap.data().siteId !== siteId) {
      return { success: false, error: 'Media not found' };
    }

    // predictionsFromFrontend should already be in the correct format:
    // { class: 'standard_id', label: 'Display Label', category: 'feature_category', ... }
    const validatedPredictions = [];
    for (const pred of predictionsFromFrontend) {
      if (!pred.class || !pred.label || !pred.category) {
        console.warn('Skipping prediction in updateMediaFeatures due to missing class, label, or category:', pred);
        continue;
      }
      validatedPredictions.push({
        class: pred.class,
        label: pred.label,
        category: pred.category,
        source: pred.source || 'manual',
        confidence: typeof pred.confidence === 'number' ? pred.confidence : 1,
        x: typeof pred.x === 'number' ? pred.x : 0,
        y: typeof pred.y === 'number' ? pred.y : 0,
        width: typeof pred.width === 'number' ? pred.width : 0,
        height: typeof pred.height === 'number' ? pred.height : 0
      });

      // Ensure the feature definition exists/is up-to-date in Firestore.
      await addFeature({
        label: pred.label,
        category: pred.category,
        source: pred.source || 'manual'
      });
    }

    await updateDoc(mediaRef, {
      predictions: validatedPredictions,
      features: deleteField() // Clean up old 'features' object if it exists
    });

    // Recalculate site-level features
    const siteRef = doc(db, 'sites', siteId);
    // It's often better to do this in a transaction or ensure atomicity if possible,
    // but for simplicity, we'll re-fetch and update.
    // Consider if this needs to be in a transaction with the media update for consistency.
    const mediaQuery = query(collection(db, 'site_media'), where('siteId', '==', siteId));
    const mediaSnap = await getDocs(mediaQuery);
    
    const aggregatedFeatures = {};
    mediaSnap.docs.forEach(doc => {
      const mediaPredictions = doc.data().predictions || [];
      mediaPredictions.forEach(pred => {
        const featureId = pred.class;
        const label = pred.label;
        const category = pred.category;
        if (!featureId || featureId === 'unknown') return;
        
        if (!aggregatedFeatures[featureId]) {
          aggregatedFeatures[featureId] = {
            enabled: true,
            autoDetected: pred.source === 'auto',
            confidence: pred.confidence || 1,
            label: label || featureId,
            category: category || 'uncategorized'
          };
        } else {
           if (pred.confidence && pred.confidence > (aggregatedFeatures[featureId].confidence || 0)) {
            aggregatedFeatures[featureId].confidence = pred.confidence;
          }
          if (aggregatedFeatures[featureId].autoDetected && pred.source !== 'auto') {
            aggregatedFeatures[featureId].autoDetected = false;
          }
          if (category && category !== 'uncategorized' && aggregatedFeatures[featureId].category === 'uncategorized') {
            aggregatedFeatures[featureId].category = category;
          }
        }
      });
    });

    await updateDoc(siteRef, {
      features: aggregatedFeatures, // Site-level summary
      updatedAt: serverTimestamp(),
      lastMediaUpdate: serverTimestamp()
    });

    return { success: true };
  } catch (error) {
    console.error('Error updating media features:', error);
    return { success: false, error: error.message || 'Failed to update media features' };
  }
};

// ... (rest of the file: deleteSiteMedia, setMainSiteMedia, updateMediaSubLocation, migrateSiteMedia, saveArticlesToMedia) ...
// Ensure these functions also use the new predictions structure if they interact with it.
// For now, focusing on addSiteMedia and updateMediaFeatures as they are most relevant to the user's feedback.

// Delete a media item
export const deleteSiteMedia = async (siteId, mediaId) => {
  try {
    const mediaRef = doc(db, 'site_media', mediaId);
    const mediaDoc = await getDoc(mediaRef);

    if (!mediaDoc.exists() || mediaDoc.data().siteId !== siteId) {
      return { success: false, error: 'Media not found' };
    }
    await deleteDoc(mediaRef);

    const siteRef = doc(db, 'sites', siteId);
    await runTransaction(db, async (transaction) => {
      const siteDoc = await transaction.get(siteRef);
      if (!siteDoc.exists()) throw new Error('Site not found');

      const mediaQuery = query(collection(db, 'site_media'), where('siteId', '==', siteId));
      const mediaSnap = await getDocs(mediaQuery); // Use getDocs
      
      const aggregatedFeatures = {};
      mediaSnap.docs.forEach(doc => {
        const mediaPredictions = doc.data().predictions || [];
        mediaPredictions.forEach(pred => {
          const featureId = pred.class; // Already standardized
          if (!featureId || featureId === 'unknown') return;
          if (!aggregatedFeatures[featureId]) {
            aggregatedFeatures[featureId] = {
              enabled: true,
              autoDetected: pred.source === 'auto',
              confidence: pred.confidence || 1,
              label: pred.label || featureId,
              category: pred.category || 'uncategorized'
            };
          } else {
            if (pred.confidence && pred.confidence > (aggregatedFeatures[featureId].confidence || 0)) {
              aggregatedFeatures[featureId].confidence = pred.confidence;
            }
             if (aggregatedFeatures[featureId].autoDetected && pred.source !== 'auto') {
              aggregatedFeatures[featureId].autoDetected = false;
            }
             if (pred.category && pred.category !== 'uncategorized' && aggregatedFeatures[featureId].category === 'uncategorized') {
              aggregatedFeatures[featureId].category = pred.category;
            }
          }
        });
      });
      transaction.update(siteRef, {
        features: aggregatedFeatures,
        updatedAt: serverTimestamp(),
        lastMediaUpdate: serverTimestamp()
      });
    });
    return { success: true };
  } catch (error) {
    console.error('Error deleting site media:', error);
    return { success: false, error: 'Failed to delete site media' };
  }
};

// Set main site media
export const setMainSiteMedia = async (siteId, mediaId) => {
  try {
    const mediaRef = doc(db, 'site_media', mediaId);
    const mediaDoc = await getDoc(mediaRef);

    if (!mediaDoc.exists() || mediaDoc.data().siteId !== siteId) {
      return { success: false, error: 'Media not found' };
    }

    const mediaQuery = query(collection(db, 'site_media'), where('siteId', '==', siteId));
    const mediaSnap = await getDocs(mediaQuery); // Use getDocs

    const updatePromises = mediaSnap.docs.map(docSnap => // docSnap from docs array
      updateDoc(docSnap.ref, { isMain: docSnap.id === mediaId })
    );
    await Promise.all(updatePromises);
    
    const siteRef = doc(db, 'sites', siteId);
    await updateDoc(siteRef, {
      primaryMediaId: mediaId,
      updatedAt: serverTimestamp(),
      lastMediaUpdate: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    console.error('Error setting main site media:', error);
    return { success: false, error: 'Failed to set main site media' };
  }
};

export const updateMediaSubLocation = async (siteId, mediaId, subLocationData) => {
  try {
    if (!siteId || !mediaId || !subLocationData) {
      return { success: false, error: 'Invalid input parameters' };
    }
    const mediaRef = doc(db, 'site_media', mediaId);
    const mediaDoc = await getDoc(mediaRef);
    if (!mediaDoc.exists() || mediaDoc.data().siteId !== siteId) {
      return { success: false, error: 'Media not found' };
    }
    await updateDoc(mediaRef, {
      subLocationId: subLocationData.subLocationId || null,
      coordinates: subLocationData.coordinates || null,
      updatedAt: serverTimestamp()
    });
    const siteRef = doc(db, 'sites', siteId);
    await updateDoc(siteRef, {
      updatedAt: serverTimestamp(),
      lastMediaUpdate: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    console.error('Error updating media sublocation:', error);
    return { success: false, error: error.message || 'Failed to update media sublocation' };
  }
};

export const migrateSiteMedia = async (siteId) => {
  try {
    const siteRef = doc(db, 'sites', siteId);
    const siteDoc = await getDoc(siteRef);
    if (!siteDoc.exists()) {
      return { success: false, error: 'Site not found' };
    }
    const siteData = siteDoc.data();
    if (!siteData.media || !Array.isArray(siteData.media)) {
      return { success: true, message: 'No media to migrate' };
    }
    const mediaPromises = siteData.media.map(item => 
      addDoc(collection(db, 'site_media'), {
        ...item,
        siteId,
        uploadedAt: item.uploadedAt ? new Date(item.uploadedAt) : serverTimestamp()
      })
    );
    await Promise.all(mediaPromises);
    await updateDoc(siteRef, {
      media: deleteField(), // Use deleteField instead of empty array for cleaner removal
      updatedAt: serverTimestamp(),
      lastMediaUpdate: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    console.error('Error migrating site media:', error);
    return { success: false, error: 'Failed to migrate site media' };
  }
};

// Removed saveArticlesToMedia as references are now embedded in the site document.
// If a generic "save article" functionality is needed elsewhere, it should be a separate, dedicated function.
